import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { StageBadge } from '../components/SeverityBadge';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

export default function Updates() {
  const { user } = useAuth();
  const [news, setNews] = useState([]);
  const [lastCrawl, setLastCrawl] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [source, setSource] = useState('all');

  const load = () => {
    api.get('/news').then(({ data }) => {
      setNews(data.items || []);
      setLastCrawl(data.last_crawled_at);
    }).catch(() => {});
  };

  useEffect(load, []);

  const triggerCrawl = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post('/news/crawl');
      toast.success(`Crawl complete — ${data.new_items_found} new items`);
      load();
    } catch (e) {
      toast.error('Manual crawl requires admin access');
    } finally {
      setRefreshing(false);
    }
  };

  const filtered = news.filter((n) => source === 'all' || n.source === source);
  const stages = ['draft_gazette', 'public_consultation', 'final_published', 'enforcement'];

  return (
    <Layout>
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-12">
        <div className="flex items-end justify-between mb-10" data-testid="updates-header">
          <div>
            <div className="label-mono">Regulatory pipeline</div>
            <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-2">FSSAI &amp; Legal Metrology updates</h1>
            <p className="text-muted-foreground mt-3 max-w-2xl">Live weekly feed from fssai.gov.in and Legal Metrology overview at consumeraffairs.gov.in. Admin can request an out-of-cycle refresh.</p>
          </div>
          <div className="text-right space-y-3">
            <div>
              <div className="label-mono">Last refresh</div>
              <div className="font-mono text-sm">{lastCrawl ? new Date(lastCrawl).toLocaleString() : 'Seed data only'}</div>
            </div>
            {user?.role === 'admin' && (
              <Button onClick={triggerCrawl} disabled={refreshing} variant="outline" className="rounded-none" data-testid="manual-crawl-btn">
                <RefreshCw size={14} strokeWidth={1.5} className={refreshing ? 'animate-spin' : ''} /> Run manual crawl
              </Button>
            )}
          </div>
        </div>

        {/* Source filter */}
        <div className="flex gap-px bg-border mb-8">
          {[['all','All sources'],['fssai','FSSAI'],['legal_metrology','Legal Metrology']].map(([k,l]) => (
            <button key={k} onClick={() => setSource(k)} data-testid={`source-filter-${k}`} className={`px-4 py-2 text-sm bg-background relative ${source === k ? 'text-foreground border-gradient-pt' : 'text-muted-foreground hover:text-foreground'}`}>{l}</button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border" data-testid="updates-pipeline">
          {stages.map((s) => {
            const items = filtered.filter((n) => n.stage === s);
            return (
              <div key={s} className="bg-background p-6 min-h-[320px]">
                <div className="flex items-center justify-between mb-4">
                  <StageBadge stage={s} />
                  <span className="font-mono text-[11px] text-muted-foreground">{items.length}</span>
                </div>
                <div className="space-y-5">
                  {items.map((n) => (
                    <a key={n.news_id} href={n.source_url || '#'} target="_blank" rel="noreferrer" className="block group" data-testid={`news-card-${n.news_id}`}>
                      <div className="label-mono-sm mb-1">{n.source === 'fssai' ? 'FSSAI' : 'LEGAL METROLOGY'} · {n.crawled_at ? new Date(n.crawled_at).toLocaleDateString() : ''}</div>
                      <div className="text-sm font-medium leading-snug group-hover:text-accent transition-colors">{n.title}</div>
                      {n.impact_summary && <div className="mt-2 text-xs text-muted-foreground leading-relaxed">{n.impact_summary}</div>}
                      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                        {n.effective_date && <span>Effective {n.effective_date}</span>}
                        {n.consultation_deadline && <span>Closes {n.consultation_deadline}</span>}
                        <ExternalLink size={12} className="ml-auto" />
                      </div>
                    </a>
                  ))}
                  {items.length === 0 && <div className="text-sm text-muted-foreground">No items.</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
